import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import { academicService, enrollmentService, messagingService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import useAuthStore from '../store/authStore';
import {
  Badge, Button, Card, EmptyState, Field, Input, Modal, Pagination,
  Select, Spinner, StatCard, Textarea,
} from '../components/ui';

const PAGE_SIZE = 20;

function studentName(student, fallback) {
  return [student?.first_name, student?.last_name].filter(Boolean).join(' ') || student?.email || fallback;
}

function formatDate(value, language) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(new Date(value));
}

function ProgressBar({ value }) {
  const percentage = Math.min(100, Math.max(0, Number(value) || 0));
  return (
    <div className="min-w-32">
      <div className="mb-1 text-xs text-gray-500">{percentage}%</div>
      <div className="h-2 overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full bg-primary-600 transition-all" style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

function TeacherStudents() {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { t, i18n } = useTranslation();
  const currentUser = useAuthStore((state) => state.user);
  const requestId = useRef(0);
  const [modules, setModules] = useState([]);
  const [moduleFilter, setModuleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [students, setStudents] = useState([]);
  const [count, setCount] = useState(0);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [messagingId, setMessagingId] = useState(null);
  const [detailsStudent, setDetailsStudent] = useState(null);
  const [commentsStudent, setCommentsStudent] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);

  useEffect(() => {
    let active = true;
    academicService.getMyModules()
      .then((res) => { if (active) setModules(Array.isArray(res.data) ? res.data : res.data?.results || []); })
      .catch(() => addNotification(t('Failed to load courses'), 'error'));
    return () => { active = false; };
  }, [addNotification, t]);

  const fetchStudents = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (moduleFilter) params.module = moduleFilter;
      if (search.trim()) params.search = search.trim();
      const res = await enrollmentService.getTeacherStudents(params);
      if (id !== requestId.current) return;
      const data = res.data || {};
      setStudents(Array.isArray(data) ? data : data.results || []);
      setCount(Array.isArray(data) ? data.length : data.count || 0);
      setSummary(Array.isArray(data) ? {} : data.summary || {});
    } catch (e) {
      if (id === requestId.current) addNotification(t('Failed to load students'), 'error');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [addNotification, moduleFilter, page, search, t]);

  useEffect(() => {
    const timer = setTimeout(fetchStudents, 300);
    return () => clearTimeout(timer);
  }, [fetchStudents]);

  const changeSearch = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const changeModule = (event) => {
    setModuleFilter(event.target.value);
    setPage(1);
  };

  const messageStudent = async (studentId) => {
    setMessagingId(studentId);
    try {
      const res = await messagingService.createPrivateConversation({ participant2: studentId });
      navigate('/messages', { state: { openConversation: { type: 'private', conversationId: res.data.id } } });
    } catch (e) {
      addNotification(t('Failed to start conversation'), 'error');
    } finally {
      setMessagingId(null);
    }
  };

  const openComments = async (student) => {
    setCommentsStudent(student);
    setComments([]);
    setCommentText('');
    setCommentsLoading(true);
    try {
      const res = await enrollmentService.getTeacherComments(student.id);
      setComments(Array.isArray(res.data) ? res.data : res.data?.results || []);
    } catch (e) {
      addNotification(t('Failed to load comments'), 'error');
    } finally {
      setCommentsLoading(false);
    }
  };

  const addComment = async () => {
    const content = commentText.trim();
    if (!content || !commentsStudent) return;
    setCommentSaving(true);
    try {
      const res = await enrollmentService.addTeacherComment(commentsStudent.id, content);
      setComments((current) => [res.data, ...current]);
      setStudents((current) => current.map((row) => (
        row.id === commentsStudent.id ? { ...row, comment_count: row.comment_count + 1 } : row
      )));
      setCommentText('');
      addNotification(t('Comment added'), 'success');
    } catch (e) {
      addNotification(t('Failed to add comment'), 'error');
    } finally {
      setCommentSaving(false);
    }
  };

  const deleteComment = async (commentId) => {
    if (!commentsStudent) return;
    setDeletingCommentId(commentId);
    try {
      await enrollmentService.deleteTeacherComment(commentsStudent.id, commentId);
      setComments((current) => current.filter((comment) => comment.id !== commentId));
      setStudents((current) => current.map((row) => (
        row.id === commentsStudent.id ? { ...row, comment_count: Math.max(0, row.comment_count - 1) } : row
      )));
      addNotification(t('Comment deleted'), 'success');
    } catch (e) {
      addNotification(t('Failed to delete comment'), 'error');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const stats = [
    { label: t('Unique students'), value: summary.unique_students ?? 0, color: 'blue', icon: <span className="text-5xl">♟</span> },
    { label: t('Active enrollments'), value: summary.active_enrollments ?? 0, color: 'green', icon: <span className="text-5xl">✓</span> },
    { label: t('Average progress'), value: `${summary.average_completion ?? 0}%`, color: 'purple', icon: <span className="text-5xl">↗</span> },
    { label: t('Average quiz score'), value: summary.average_quiz_score == null ? '—' : `${summary.average_quiz_score}%`, color: 'yellow', icon: <span className="text-5xl">★</span> },
  ];

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <TopNav title={t('My Students')} subtitle={t('Student progress, attendance, work and private notes')} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {stats.map((stat) => <StatCard key={stat.label} {...stat} />)}
          </div>

          <Card padded={false}>
            <div className="grid gap-3 border-b border-gray-100 p-4 sm:grid-cols-2">
              <Input value={search} onChange={changeSearch} placeholder={t('Search students by name or email...')} />
              <Select value={moduleFilter} onChange={changeModule}>
                <option value="">{t('All my courses')}</option>
                {modules.map((module) => <option key={module.id} value={module.id}>{module.name}</option>)}
              </Select>
            </div>

            {loading ? (
              <div className="flex justify-center p-12"><Spinner /></div>
            ) : students.length === 0 ? (
              <EmptyState
                title={search ? t('No students match your search') : t('No active students yet')}
                description={search ? t('Try another name, email, or course') : t('Students will show up once enrolled')}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1180px] text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 text-start">{t('Student')}</th>
                      <th className="px-4 py-3 text-start">{t('Course')}</th>
                      <th className="px-4 py-3 text-start">{t('Progress')}</th>
                      <th className="px-4 py-3 text-start">{t('Quizzes')}</th>
                      <th className="px-4 py-3 text-start">{t('Assignments')}</th>
                      <th className="px-4 py-3 text-start">{t('Attendance')}</th>
                      <th className="px-4 py-3 text-end">{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {students.map((row) => (
                      <tr key={row.id} className="align-top hover:bg-gray-50/70">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            {row.student?.profile_picture ? (
                              <img src={row.student.profile_picture} alt="" className="h-10 w-10 rounded-full object-cover" />
                            ) : (
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 font-semibold text-primary-700">
                                {(row.student?.first_name?.[0] || row.student?.email?.[0] || '?').toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900">{studentName(row.student, t('Student'))}</p>
                              <p className="text-xs text-gray-500">{row.student?.email}</p>
                              {row.student?.phone && <p className="text-xs text-gray-400">{row.student.phone}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <button type="button" className="font-medium text-primary-700 hover:underline" onClick={() => navigate(`/modules/${row.module}`)}>{row.module_name}</button>
                          <p className="mt-1 text-xs text-gray-500">{row.grade_name || '—'}</p>
                          <Badge status={row.status} />
                        </td>
                        <td className="px-4 py-4">
                          <ProgressBar value={row.completion_percentage} />
                          <p className="mt-1 text-xs text-gray-500">{t('{{completed}} of {{total}} lessons', { completed: row.completed_lessons, total: row.total_lessons })}</p>
                        </td>
                        <td className="px-4 py-4 text-gray-700">
                          <p className="font-medium">{row.average_quiz_score == null ? '—' : `${row.average_quiz_score}%`}</p>
                          <p className="text-xs text-gray-500">{t('{{count}} attempted', { count: row.quizzes_attempted })}</p>
                        </td>
                        <td className="px-4 py-4 text-gray-700">
                          <p className="font-medium">{row.assignments_submitted}</p>
                          <p className="text-xs text-gray-500">{t('submitted')}</p>
                        </td>
                        <td className="px-4 py-4 text-gray-700">
                          <p className="font-medium">{row.attendance_percentage == null ? '—' : `${row.attendance_percentage}%`}</p>
                          <p className="text-xs text-gray-500">{t('{{present}} of {{total}} present', { present: row.attendance_present, total: row.attendance_total })}</p>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button size="sm" variant="secondary" onClick={() => setDetailsStudent(row)}>{t('Details')}</Button>
                            <Button size="sm" variant="secondary" onClick={() => openComments(row)}>{t('Comments')} ({row.comment_count})</Button>
                            <Button size="sm" variant="secondary" onClick={() => navigate(`/modules/${row.module}?tab=students&enrollment=${row.id}`)}>{t('Course record')}</Button>
                            <Button size="sm" loading={messagingId === row.student?.id} onClick={() => messageStudent(row.student?.id)}>{t('Message')}</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} />
          </Card>
        </main>
      </div>

      <Modal open={Boolean(detailsStudent)} onClose={() => setDetailsStudent(null)} title={t('Student details')} size="lg">
        {detailsStudent && (
          <div className="space-y-5">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">{studentName(detailsStudent.student, t('Student'))}</h4>
              <p className="text-sm text-gray-500">{detailsStudent.student?.email}{detailsStudent.student?.phone ? ` · ${detailsStudent.student.phone}` : ''}</p>
              <p className="mt-1 text-sm font-medium text-primary-700">{detailsStudent.module_name}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard label={t('Progress')} value={`${detailsStudent.completion_percentage}%`} color="purple" />
              <StatCard label={t('Quiz average')} value={detailsStudent.average_quiz_score == null ? '—' : `${detailsStudent.average_quiz_score}%`} color="yellow" />
              <StatCard label={t('Assignments')} value={detailsStudent.assignments_submitted} color="blue" />
              <StatCard label={t('Attendance')} value={detailsStudent.attendance_percentage == null ? '—' : `${detailsStudent.attendance_percentage}%`} color="green" />
            </div>
            <dl className="grid gap-4 rounded-xl bg-gray-50 p-4 sm:grid-cols-2">
              <div><dt className="text-xs text-gray-500">{t('Enrollment date')}</dt><dd className="mt-1 font-medium">{formatDate(detailsStudent.created_at, i18n.language)}</dd></div>
              <div><dt className="text-xs text-gray-500">{t('Access ends')}</dt><dd className="mt-1 font-medium">{formatDate(detailsStudent.end_date, i18n.language)}</dd></div>
              <div><dt className="text-xs text-gray-500">{t('Lessons completed')}</dt><dd className="mt-1 font-medium">{detailsStudent.completed_lessons} / {detailsStudent.total_lessons}</dd></div>
              <div><dt className="text-xs text-gray-500">{t('Quizzes attempted')}</dt><dd className="mt-1 font-medium">{detailsStudent.quizzes_attempted}</dd></div>
            </dl>
            <div>
              <p className="text-sm font-medium text-gray-700">{t('Student enrollment note')}</p>
              <p className="mt-2 whitespace-pre-wrap rounded-xl border border-gray-100 p-3 text-sm text-gray-600">{detailsStudent.student_note || t('No enrollment note')}</p>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(commentsStudent)} onClose={() => setCommentsStudent(null)} title={t('Private teacher comments')} size="lg">
        {commentsStudent && (
          <div className="space-y-5">
            <p className="text-sm text-gray-600">{studentName(commentsStudent.student, t('Student'))} · {commentsStudent.module_name}</p>
            <Field label={t('Add a private comment')} hint={t('Only teachers assigned to this course can see these comments.')}>
              <Textarea rows={4} maxLength={4000} value={commentText} onChange={(event) => setCommentText(event.target.value)} placeholder={t('Write a comment about this student...')} />
            </Field>
            <div className="flex justify-end"><Button loading={commentSaving} disabled={!commentText.trim()} onClick={addComment}>{t('Add comment')}</Button></div>
            <div className="border-t border-gray-100 pt-4">
              {commentsLoading ? (
                <div className="flex justify-center p-6"><Spinner /></div>
              ) : comments.length === 0 ? (
                <p className="py-5 text-center text-sm text-gray-500">{t('No comments yet')}</p>
              ) : (
                <div className="space-y-3">
                  {comments.map((comment) => (
                    <div key={comment.id} className="rounded-xl bg-gray-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{comment.author_name}</p>
                          <p className="text-xs text-gray-400">{formatDate(comment.created_at, i18n.language)}</p>
                        </div>
                        {comment.author === currentUser?.id && (
                          <Button size="sm" variant="danger" loading={deletingCommentId === comment.id} onClick={() => deleteComment(comment.id)}>{t('Delete')}</Button>
                        )}
                      </div>
                      <p className="mt-3 whitespace-pre-wrap text-sm text-gray-700">{comment.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default TeacherStudents;
